import { useCallback, useEffect, useRef, useState } from 'react';
import type { AppLanguage } from '../i18n/types';
import type { AssetSearchResult, AssetSearchUiState } from '../api/assetSearchClient';
import {
  ASSET_SEARCH_PAGE_SIZE,
  fetchSearchAssetsForUiState,
  fetchSearchAssetsPage,
  hasClientSideBrowseFilters,
  invalidateActiveAssetCatalogCache,
} from '../api/assetSearchClient';
import {
  isAssetKeywordQueryActive,
  refineAssetKeywordSearchResults,
} from '../utils/assetKeywordSearch';
import { registerAssetThumbnailRecords } from '../utils/assetThumbnailStore';
import { assetSearchUiStateKey } from '../utils/assetSearchUiState';

export type UseBookableAssetSearchOptions = {
  enabled?: boolean;
  /** Debounce ms for text-driven refetch (0 = immediate). */
  debounceMs?: number;
  language?: AppLanguage;
};

export type UseBookableAssetSearchState = {
  records: Record<string, unknown>[];
  total: number;
  loading: boolean;
  loadingMore: boolean;
  catalogExpanding: boolean;
  error: string | null;
  hasMore: boolean;
  searchMode: boolean;
  /** True once the fetch for the current filters finished (including the full catalog expand). */
  settled: boolean;
  refresh: () => void;
  loadMore: () => void;
};

export function useBookableAssetSearch(
  uiState: AssetSearchUiState,
  options: UseBookableAssetSearchOptions = {},
): UseBookableAssetSearchState {
  const { enabled = true, debounceMs = 0, language = 'en' } = options;
  const [records, setRecords] = useState<Record<string, unknown>[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [catalogExpanding, setCatalogExpanding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [settledKey, setSettledKey] = useState<string | null>(null);
  const fetchGenRef = useRef(0);
  const recordsRef = useRef<Record<string, unknown>[]>([]);
  const uiStateRef = useRef(uiState);
  uiStateRef.current = uiState;
  const uiStateKey = assetSearchUiStateKey(uiState);
  const keywordActive = isAssetKeywordQueryActive(uiState.text ?? '');
  const searchMode = hasClientSideBrowseFilters(uiState) || keywordActive;

  const applyResult = useCallback(
    (result: AssetSearchResult, append: boolean) => {
      const state = uiStateRef.current;
      const query = state.text?.trim() ?? '';
      if (result.records.length > 0) {
        registerAssetThumbnailRecords(result.records);
      }
      const merged = append ? [...recordsRef.current, ...result.records] : result.records;
      // ≥3-char keyword: exact substring / prefix match (and light fuzzy) on the client.
      // Catalog dump is the source — ES `text` alone often misses names like onobiz for "ono".
      const refined = isAssetKeywordQueryActive(query)
        ? refineAssetKeywordSearchResults(merged, query, language)
        : merged;
      recordsRef.current = refined;
      setRecords(refined);
      setTotal(isAssetKeywordQueryActive(query) ? refined.length : result.total);
      setHasMore(isAssetKeywordQueryActive(query) ? false : result.hasMore);
      setCatalogExpanding(Boolean(result.catalogExpanding));
    },
    [language],
  );

  const runFetch = useCallback(
    async (append: boolean, from: number, forceRefresh = false) => {
      const state = uiStateRef.current;
      const keyword = isAssetKeywordQueryActive(state.text ?? '');
      const catalogMode = hasClientSideBrowseFilters(state) && !keyword;
      const gen = ++fetchGenRef.current;
      const fetchKey = assetSearchUiStateKey(state);
      let finalApplied = false;
      let completedPartial: AssetSearchResult | null = null;
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setError(null);
        setSettledKey(null);
      }

      try {
        const result =
          append && !keyword && !catalogMode
            ? await fetchSearchAssetsPage(state, from, ASSET_SEARCH_PAGE_SIZE)
            : await fetchSearchAssetsForUiState(state, {
                from,
                forceRefresh,
                onPartial: catalogMode
                  ? (partial) => {
                      if (gen !== fetchGenRef.current) {
                        return;
                      }
                      applyResult(partial, false);
                      setLoading(false);
                      if (!partial.catalogExpanding) {
                        completedPartial = partial;
                        if (finalApplied) {
                          setSettledKey(fetchKey);
                        }
                      }
                    }
                  : undefined,
              });
        if (gen !== fetchGenRef.current) {
          return;
        }
        // The catalog can finish expanding while the first stage is still being filtered.
        const finalResult = result.catalogExpanding && completedPartial ? completedPartial : result;
        applyResult(finalResult, append);
        finalApplied = true;
        if (!finalResult.catalogExpanding) {
          setSettledKey(fetchKey);
        }
      } catch (err) {
        if (gen !== fetchGenRef.current) {
          return;
        }
        if (!append) {
          recordsRef.current = [];
          setRecords([]);
          setTotal(0);
          setHasMore(false);
          setCatalogExpanding(false);
        }
        setError(err instanceof Error ? err.message : String(err));
        setSettledKey(fetchKey);
      } finally {
        if (gen === fetchGenRef.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [applyResult],
  );

  const refresh = useCallback(() => {
    invalidateActiveAssetCatalogCache();
    void runFetch(false, 0, true);
  }, [runFetch]);

  const loadMore = useCallback(() => {
    if (loading || loadingMore || !hasMore || searchMode) {
      return;
    }
    void runFetch(true, recordsRef.current.length);
  }, [hasMore, loading, loadingMore, runFetch, searchMode]);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    if (debounceMs <= 0) {
      void runFetch(false, 0);
      return;
    }
    const timer = window.setTimeout(() => {
      void runFetch(false, 0);
    }, debounceMs);
    return () => window.clearTimeout(timer);
  }, [enabled, uiStateKey, debounceMs, runFetch]);

  return {
    records,
    total,
    loading,
    loadingMore,
    catalogExpanding,
    error,
    hasMore,
    searchMode,
    settled: settledKey === uiStateKey && !loading,
    refresh,
    loadMore,
  };
}
